import { stage } from 'config';

import { FC, useEffect, useMemo } from 'react';
// Icons
import { FaFacebook, FaLinkedin, FaFacebookMessenger, FaWhatsapp } from 'react-icons/fa';
import { FaXTwitter, FaThreads } from "react-icons/fa6";
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Models
import IQuiz from 'models/Quiz';
// Types
import QuizTypes from 'types/QuizTypes';
// Async
import { createAnalyticsInteract } from 'store/player/playerAsync';
// Selectors
import { selectResult } from 'store/player/playerSelectors';
import { selectQuiz } from 'store/quiz/quizSelectors';
// Components
import { Button } from 'components/Buttons';
// Service
import DeviceService from 'services/Device.service';
// i18next
import { useTranslation } from 'react-i18next';

// const gtag = (window as any).gtag;

let anchorElem:HTMLAnchorElement | null = null;

const SocialShare:FC = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const result:any = useAppSelector(selectResult);
  const quiz:IQuiz = useAppSelector(selectQuiz);

  const { _id:resultId, score, position } = result?.currentUserResult || {};
  const { resultsPage, quizNameSlug, type: quizType } = result?.quiz || quiz;

  useEffect(() => {
    if (stage !== 'development') return;
    (window as any).fbAsyncInit = () => {
      (window as any).FB.init({
        appId: process.env.REACT_APP_FACEBOOK_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v20.0'
      });
    };
    (function (d,s,id){
      let js:any;
      let fjs:any = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) { return; }
      js = d.createElement(s); js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
    return () => {
      if ( anchorElem ) document.body.removeChild(anchorElem);
    }
    // eslint-disable-next-line
  }, [])

  const shareText = useMemo(() => {
    if (quizType === QuizTypes.Prediction) return t('components.socialShare.shareTextPrediction', { brandName: quiz.brand || '' });
    if ([QuizTypes.Poll, QuizTypes.RankingPoll].includes(quizType)) return t('components.socialShare.shareTextPoll', { brandName: quiz.brand || '', quizName: quiz.quizName });
    return t('components.socialShare.shareText', { score, brandName: quiz.brand || '', position });
    // eslint-disable-next-line
  }, []);

  if (!resultsPage?.socialShare || !resultsPage?.socialShare?.enabled) return null;

  const shareUrl = resultsPage.socialShare.url || window.location.origin + `/quiz/${quizNameSlug}`;
  const hashtags = resultsPage.socialShare.hashtags || '';

  const handleShare = async (name:string) => {
    try {
      await dispatch(createAnalyticsInteract({ result: resultId, name }));
      // await dispatch(increaseScore({
      //   resultId,
      //   data: {
      //     numberOfPoints: config.socialShareBonusValue,
      //     interaction
      //   }
      // }))
    } catch(error){}
  }

  const handleFacebookShare = () => {
    handleWindowOpen(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&hashtag=${encodeURIComponent(hashtags.split(' ').shift())}`);
    // (window as any).FB.ui({
    //   method: 'share',
    //   href: shareUrl,
    //   quote: shareText,
    //   hashtag: hashtags.split(' ').shift(),
    // }, () => {});
    // gtag('event', 'shared-on-facebook', {
    //   'event_category': 'Social sharing',
    //   'event_label': 'After quiz',
    // });
    handleShare('facebookShare');
  }

  const handleTwitterShare = () => {
    handleWindowOpen(`http://twitter.com/share?text=${encodeURIComponent(`${shareText} ${hashtags}`)}&url=${encodeURIComponent(shareUrl)}`);
    // gtag('event', 'shared-on-twitter', {
    //   'event_category': 'Social sharing',
    //   'event_label': 'After quiz',
    // });
    handleShare('twitterShare');
  };

  const handleLinkedInShare = () => {
    handleWindowOpen(`https://www.linkedin.com/sharing/share-offsite?url=${encodeURIComponent(shareUrl)}`);
    // gtag('event', 'shared-on-linkedin', {
    //   'event_category': 'Social sharing',
    //   'event_label': 'After quiz'
    // });
    handleShare('linkedInShare');
  };

  const handleMessangerShare = () => {
    if ( DeviceService.isAny() ){
      handleWindowOpen(`fb-messenger://share?link=${encodeURIComponent(shareUrl)}`);
    } else {
      if (stage !== 'development') return;
      (window as any).FB.ui({
        method: 'send',
        link: shareUrl,
        quote: shareText,
        hashtag: hashtags.split(' ').shift(),
      }, () => {});
    }
    // gtag('event', 'shared-on-messanger', {
    //   'event_category': 'Social sharing',
    //   'event_label': 'After quiz',
    // });
    handleShare('messengerShare');
  }

  const handleWhatsappShare = () => {
    if ( DeviceService.isAny() ){
      handleWindowOpen(`whatsapp://send?text=${encodeURIComponent(`${shareText} ${hashtags} ${shareUrl}`)}`);
    } else {
      handleWindowOpen(`https://wa.me?text=${encodeURIComponent(`${shareText} ${hashtags} ${shareUrl}`)}`);
    }
    // gtag('event', 'shared-on-whatsapp', {
    //   'event_category': 'Social sharing',
    //   'event_label': 'After quiz',
    // });
    handleShare('whatsAppShare');
  }

  const handleThreadsShare = () => {
    handleWindowOpen(`https://threads.net/intent/post?text=${encodeURIComponent(`${shareText} ${hashtags} ${shareUrl}`)}`);
    handleShare('threadsShare');
  }

  const handleWindowOpen = (href:string) => {
    if ( anchorElem ) document.body.removeChild(anchorElem);
    anchorElem = document.createElement('a');
    anchorElem.target = '_blank';
    anchorElem.href = href;
    document.body.appendChild(anchorElem);
    anchorElem.click();
  }

  return (
    <div className="flex justify-around gap-2">
      <Button
        className="bg-transparent"
        disableShadow
        onClick={handleFacebookShare}
        size="xsmall"
      ><FaFacebook style={{ color: '#fff' }} size={28} /></Button>
      <Button
        className="bg-transparent"
        disableShadow
        onClick={handleTwitterShare}
        size="xsmall"
      ><FaXTwitter style={{ color: '#fff' }} size={28} /></Button>
      <Button
        className="bg-transparent"
        disableShadow
        onClick={handleLinkedInShare}
        size="xsmall"
      ><FaLinkedin style={{ color: '#fff' }} size={28} /></Button>
      {(DeviceService.isAny() || stage === 'development') ? (
        <Button
        className="bg-transparent"
        disableShadow
        onClick={handleMessangerShare}
        size="xsmall"
        ><FaFacebookMessenger style={{ color: '#fff' }} size={28} /></Button>
      ) : null}
      <Button
        className="bg-transparent"
        disableShadow
        onClick={handleWhatsappShare}
        size="xsmall"
      ><FaWhatsapp style={{ color: '#fff' }} size={28} /></Button>
      <Button
        className="bg-transparent"
        disableShadow
        onClick={handleThreadsShare}
        size="xsmall"
      ><FaThreads style={{ color: '#fff' }} size={28} /></Button>
    </div>
  )
}

export default SocialShare;
